import {Injectable} from '@angular/core';
import {Actions, concatLatestFrom, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {mappedHttpErrorResponseOperator} from '@adnova/jf-ng-components';
import {catchError, groupBy, map, mergeMap, of, switchMap} from 'rxjs';
import {BelegService} from '../../openapi/fakturierung-openapi';
import {BelegKetteActions} from '../actions/beleg-kette.actions';
import {FakturierungsbelegEntitiesActions} from '../actions/fakturierungsbeleg-entities.actions';
import {BelegKetteSelectors} from '../selectors/beleg-kette.selectors';
import {AppState} from '../states/app.state';


function belegIds(...ids: (string | undefined)[]): string[] {
  return ids.filter((id): id is string => !!id);
}


/**
 * Laedt ueber den separaten kette-Endpoint die Belegkette eines Belegs und laedt alle bereits
 * geladenen Ketten neu, sobald Konvertierung, Storno oder Loeschen einen ihrer Belege veraendern.
 */
@Injectable()
export class BelegKetteEffects {

  constructor(
    private readonly actions$: Actions,
    private readonly belegService: BelegService,
    private readonly store: Store<AppState>,
  ) {
  }

  readonly loadBelegKette$ = createEffect(() => this.actions$.pipe(
    ofType(BelegKetteActions.loadBelegKette),
    // INFO: pro Beleg eigener switchMap — ein Beleg-Wechsel darf den Request eines anderen
    //       Belegs nicht wegcanceln, ein erneuter Load desselben Belegs schon.
    groupBy(({belegId}) => belegId),
    mergeMap(perBeleg$ => perBeleg$.pipe(
      switchMap(({betriebId, belegId}) =>
        this.belegService.readBelegKette(betriebId, belegId).pipe(
          map(kette => BelegKetteActions.loadBelegKetteSuccess({belegId, kette})),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(mappedError => BelegKetteActions.loadBelegKetteFailure({belegId, error: mappedError})),
          )),
        ),
      ),
    )),
  ));

  readonly invalidateNachKonvertierung$ = createEffect(() => this.actions$.pipe(
    ofType(FakturierungsbelegEntitiesActions.konvertiereBelegSuccess),
    concatLatestFrom(({zielDto, quellDto}) => this.store.select(BelegKetteSelectors.geladeneKettenMit(
      belegIds(quellDto?.id ?? zielDto.quelle?.belegId, zielDto.id),
    ))),
    mergeMap(([{zielDto}, ketten]) => this.loadKetten(zielDto.betriebId, ketten)),
  ));

  readonly invalidateNachStorno$ = createEffect(() => this.actions$.pipe(
    ofType(FakturierungsbelegEntitiesActions.cancelFakturierungsbelegSuccess),
    concatLatestFrom(({cancelledBeleg}) => this.store.select(BelegKetteSelectors.geladeneKettenMit(
      belegIds(cancelledBeleg.quelle?.belegId, cancelledBeleg.id, cancelledBeleg.ziel?.belegId),
    ))),
    mergeMap(([{cancelledBeleg}, ketten]) => this.loadKetten(cancelledBeleg.betriebId, ketten)),
  ));

  readonly invalidateNachLoeschen$ = createEffect(() => this.actions$.pipe(
    ofType(FakturierungsbelegEntitiesActions.deleteFakturierungsbelegSuccess),
    concatLatestFrom(({belegId}) => this.store.select(BelegKetteSelectors.geladeneKettenMit([belegId]))),
    mergeMap(([{betriebId}, ketten]) => this.loadKetten(betriebId, ketten)),
  ));

  private loadKetten(betriebId: string | null | undefined, kettenBelegIds: string[]) {
    if (!betriebId) {
      return [];
    }
    return kettenBelegIds.map(belegId => BelegKetteActions.loadBelegKette({betriebId, belegId}));
  }
}
